import { useCallback, useState } from "react";
import PromptDialog from "@entrilia/shared/components/dialog/PromptDialog.tsx";
import { defined } from "@entrilia/shared/utilities/typeHelper.ts";
import type { ReportGroup } from "../../../../../../api/types/svTypes.ts";
import ReportGroupsConnector from "../../reports/components/ReportGroupsConnector.tsx";
import { useGroupsContext } from "../contexts/GroupsContext.ts";
import ReportGroupForm from "./ReportGroupForm.tsx";

interface Props {
  clientCode: string;
  onAdded?: (group: ReportGroup) => void;
  onClose: () => void;
}

const AddGroupDialog = (props: Props) => {
  const { clientCode, onAdded, onClose } = props;

  const { groups: reportGroups, actions: groupActions } = useGroupsContext();

  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);

  const onSave = useCallback(
    // eslint-disable-next-line @eslint-react/immutability -- groupActions is a service object; calling its methods mutates nothing React owns
    (caption: string) => {
      setSaving(true);
      let maxOrder = 0;
      defined(reportGroups[clientCode]).forEach((rg) => {
        maxOrder = Math.max(maxOrder, rg.order);
      });

      // eslint-disable-next-line @eslint-react/immutability -- see above
      groupActions
        .add(clientCode, { caption, order: maxOrder + 1 } as ReportGroup)
        .then((newGroup) => {
          if (!newGroup) {
            setError("An error occurred while creating a new report group.");
            setSaving(false);
            return;
          }
          onAdded?.(newGroup);
          onClose();
        })
        .catch(() => {
          setError("An error occurred while creating a new report group.");
          setSaving(false);
        });
    },
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    [reportGroups, onAdded, onClose]
  );

  return (
    <PromptDialog open={true} sx={{ minHeight: "auto" }}>
      <ReportGroupsConnector clientCode={clientCode}>
        <ReportGroupForm
          group={{ caption: "" } as ReportGroup}
          title="New Group"
          saving={saving}
          onCancel={onClose}
          error={error}
          onSave={onSave}
        />
      </ReportGroupsConnector>
    </PromptDialog>
  );
};

export default AddGroupDialog;
